<template>
    <div class="container mt-4">
        <div class="mb-3">
            <h1>Scan result</h1>
        </div>

        <!-- Loader -->
        <div v-if="isLoading">
            <output class="spinner-grow spinner-grow-sm text-primary"></output>
            <span class="ms-2">Loading scan result, please wait...</span>
        </div>

        <div v-if="!isLoading">
            <div class="mb-4">
                <h2 class="text-primary">Domains scanned</h2>
                <div v-for="(domain, index) in scanResult.domains" :key="index">
                    <span class="badge text-bg-info">
                        <i class="fa-solid fa-globe"></i>
                        {{ domain }}
                    </span>
                </div>
            </div>

            <div v-if="scanResult?.email_security" class="mb-4">
                <h2 class="text-primary">Email security</h2>

                <div v-if="scanResult?.email_security?.message === 'Scan not completed'">
                    <div v-if="scanStatus.email_security_scan_status == 'to_be_initiated'">
                        <output class="spinner-grow spinner-grow-sm text-primary"></output>
                        <span class="ms-2">To be initiated...</span>
                    </div>
                    <div v-if="scanStatus.email_security_scan_status == 'in_progress'">
                        <output class="spinner-grow spinner-grow-sm text-warning"></output>
                        <span class="ms-2">In progress...</span>
                    </div>
                </div>

                <div v-else>
                    <div>
                        <table class="table table-bordered">
                            <tbody>
                                <tr v-for="email_security, domain in scanResult.email_security" :key="domain">
                                    <td style="width:50%;">{{ domain }}</td>
                                    <td>
                                        <span v-if="email_security.has_spf"
                                            class="badge text-bg-success me-2">
                                            SPF
                                        </span>
                                        <span v-else class="badge text-bg-danger me-2">
                                            SPF
                                        </span>

                                        <span v-if="email_security.has_dmarc" class="badge text-bg-success">
                                            DMARC
                                        </span>
                                        <span v-else class="badge text-bg-danger">
                                            DMARC
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div v-if="scanResult?.dnssec" class="mb-4">
                <h2 class="text-primary">DNS Security</h2>

                <div v-if="scanResult?.dnssec?.message === 'Scan not completed'">
                    <div v-if="scanStatus.dnssec_scan_status == 'to_be_initiated'">
                        <output class="spinner-grow spinner-grow-sm text-primary"></output>
                        <span class="ms-2">To be initiated...</span>
                    </div>
                    <div v-if="scanStatus.dnssec_scan_status == 'in_progress'">
                        <output class="spinner-grow spinner-grow-sm text-warning"></output>
                        <span class="ms-2">In progress...</span>
                    </div>
                </div>

                <div v-else>
                    <div>
                        <table class="table table-bordered">
                            <tbody>
                                <tr v-for="dnssec, domain in scanResult.dnssec" :key="domain">
                                    <td style="width:50%;">{{ domain }}</td>
                                    <td>
                                        <span v-if="dnssec.has_dnssec" class="badge text-bg-success me-2">
                                            DNSSEC
                                        </span>
                                        <span v-else class="badge text-bg-danger me-2">
                                            DNSSEC
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div class="mb-4">
                <h2 class="text-primary">Public systems</h2>
                <div v-if="scanResult?.public_systems?.message === 'Scan not completed'">
                    <div v-if="scanStatus.public_system_discovery_status == 'to_be_initiated'">
                        <output class="spinner-grow spinner-grow-sm text-primary"></output>
                        <span class="ms-2">To be initiated...</span>
                    </div>
                    <div v-if="scanStatus.public_system_discovery_status == 'in_progress'">
                        <output class="spinner-grow spinner-grow-sm text-warning"></output>
                        <span class="ms-2">In progress...</span>
                    </div>
                </div>

                <div v-else>
                    <div>
                        <table class="table table-bordered">
                            <thead>
                                <tr>
                                    <th style="width:50%;">Domain</th>
                                    <th>Public Systems</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="public_systems, domain in scanResult.public_systems" :key="domain">
                                    <td>{{ domain }}</td>
                                    <td>
                                        <div v-if="public_systems.length > 0">
                                            <div v-for="(public_system, index) in public_systems" :key="index">
                                                <span class="badge text-bg-info">
                                                    <i class="fa-solid fa-server"></i>
                                                    {{ public_system }}
                                                </span>
                                            </div>
                                        </div>
                                        <div v-else>
                                            <span class="badge text-bg-info">No public systems identified</span>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div class="mb-4">
                <h2 class="text-primary">Harvested emails</h2>

                <div v-if="scanResult?.harvested_emails?.message === 'Scan not completed'">
                    <div v-if="scanStatus.email_harvesting_status == 'to_be_initiated'">
                        <output class="spinner-grow spinner-grow-sm text-primary"></output>
                        <span class="ms-2">To be initiated...</span>
                    </div>
                    <div v-if="scanStatus.email_harvesting_status == 'in_progress'">
                        <output class="spinner-grow spinner-grow-sm text-warning"></output>
                        <span class="ms-2">In progress...</span>
                    </div>
                </div>

                <div v-else>
                    <div>
                        <table class="table table-bordered">
                            <thead>
                                <tr>
                                    <th style="width:50%;">Domain</th>
                                    <th>Harvested emails</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="harvested_emails, domain in scanResult.harvested_emails" :key="domain">
                                    <td>{{ domain }}</td>
                                    <td>
                                        <div v-if="harvested_emails.length > 0">
                                            <div v-for="(harvested_email, index) in harvested_emails" :key="index">
                                                <span class="badge text-bg-info">
                                                    <i class="fa-solid fa-envelope"></i>
                                                    {{ harvested_email }}
                                                </span>
                                            </div>
                                        </div>
                                        <div v-else>
                                            <span class="badge text-bg-info">No emails harvested</span>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div v-if="scanResult?.security_headers" class="mb-4">
                <h2 class="text-primary">Security headers</h2>

                <div v-if="scanResult?.security_headers?.message === 'Scan not completed'">
                    <div v-if="scanStatus.security_headers_scan_status == 'to_be_initiated'">
                        <output class="spinner-grow spinner-grow-sm text-primary"></output>
                        <span class="ms-2">To be initiated...</span>
                    </div>
                    <div v-if="scanStatus.security_headers_scan_status == 'in_progress'">
                        <output class="spinner-grow spinner-grow-sm text-warning"></output>
                        <span class="ms-2">In progress...</span>
                    </div>
                </div>

                <div v-else>
                    <div>
                        <table class="table table-bordered">
                            <tbody>
                                <tr v-for="security_headers, public_system in scanResult.security_headers"
                                    :key="public_system">
                                    <td style="width:50%;">{{ public_system }}</td>
                                    <td>
                                        <span v-if="security_headers.has_content_security_policy"
                                            class="badge text-bg-success me-2">
                                            Content security policy
                                        </span>
                                        <span v-else class="badge text-bg-danger me-2">
                                            Content security policy
                                        </span>
                                        <br>
                                        <span v-if="security_headers.has_strict_transport_security"
                                            class="badge text-bg-success me-2">Strict transport security</span>
                                        <span v-else class="badge text-bg-danger me-2">Strict transport security</span>
                                        <br>
                                        <span v-if="security_headers.has_x_frame_options"
                                            class="badge text-bg-success me-2">X frame options</span>
                                        <span v-else class="badge text-bg-danger me-2">X frame options</span>
                                        <br>
                                        <span v-if="security_headers.has_x_content_type_options"
                                            class="badge text-bg-success me-2">X content type options</span>
                                        <span v-else class="badge text-bg-danger me-2">X content type options</span>
                                        <br>
                                        <span v-if="security_headers.has_referrer_policy"
                                            class="badge text-bg-success me-2">Referrer policy</span>
                                        <span v-else class="badge text-bg-danger me-2">Referrer policy</span>
                                        <br>
                                        <span v-if="security_headers.has_permissions_policy"
                                            class="badge text-bg-success me-2">Permissions policy</span>
                                        <span v-else class="badge text-bg-danger me-2">Permissions policy</span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div v-if="scanResult?.https" class="mb-4">
                <h2 class="text-primary">HTTPS</h2>

                <div v-if="scanResult?.https?.message === 'Scan not completed'">
                    <div v-if="scanStatus.https_scan_status == 'to_be_initiated'">
                        <output class="spinner-grow spinner-grow-sm text-primary"></output>
                        <span class="ms-2">To be initiated...</span>
                    </div>
                    <div v-if="scanStatus.https_scan_status == 'in_progress'">
                        <output class="spinner-grow spinner-grow-sm text-warning"></output>
                        <span class="ms-2">In progress...</span>
                    </div>
                </div>

                <div v-else>
                    <div>
                        <table class="table table-bordered">
                            <tbody>
                                <tr v-for="https, public_system in scanResult.https" :key="public_system">
                                    <td style="width:50%;">{{ public_system }}</td>
                                    <td>
                                        <div>
                                            <span v-if="https.has_https" class="badge text-bg-success me-2">HTTPS</span>
                                            <span v-else class="badge text-bg-danger me-2">HTTPS</span>
                                        </div>

                                        <div>
                                            <span v-if="https.has_valid_cert" class="badge text-bg-success me-2">Has
                                                valid
                                                cert</span>
                                            <div v-else>
                                                <span class="badge text-bg-danger me-2">Invalid cert</span><span>({{
                                                    https.invalid_cert_reason }})</span>
                                            </div>
                                        </div>

                                        <div>
                                            <span v-if="https.has_sslv2" class="badge text-bg-danger me-2">SSL 2.0
                                                enabled</span>
                                            <span v-else class="badge text-bg-success me-2">SSL 2.0 disabled</span>
                                        </div>

                                        <div>
                                            <span v-if="https.has_sslv3" class="badge text-bg-danger me-2">SSL 3.0
                                                enabled</span>
                                            <span v-else class="badge text-bg-success me-2">SSL 3.0 disabled</span>
                                        </div>

                                        <div>
                                            <span v-if="https.has_tlsv10" class="badge text-bg-danger me-2">TLS 1.0
                                                enabled</span>
                                            <span v-else class="badge text-bg-success me-2">TLS 1.0 disabled</span>
                                        </div>

                                        <div>
                                            <span v-if="https.has_tlsv11" class="badge text-bg-danger me-2">TLS 1.1
                                                enabled</span>
                                            <span v-else class="badge text-bg-success me-2">TLS 1.1 disabled</span>
                                        </div>

                                        <div>
                                            <span v-if="https.has_tlsv12" class="badge text-bg-success me-2">TLS 1.2
                                                enabled</span>
                                            <span v-else class="badge text-bg-danger me-2">TLS 1.1 disabled</span>
                                        </div>

                                        <div>
                                            <span v-if="https.has_tlsv13" class="badge text-bg-success me-2">TLS 1.3
                                                enabled</span>
                                            <span v-else class="badge text-bg-danger me-2">TLS 1.3 disabled</span>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div v-if="scanResult?.reputation" class="mb-4">
                <h2 class="text-primary">Reputation</h2>

                <div v-if="scanResult?.reputation?.message === 'Scan not completed'">
                    <div v-if="scanStatus.reputation_scan_status == 'to_be_initiated'">
                        <output class="spinner-grow spinner-grow-sm text-primary"></output>
                        <span class="ms-2">To be initiated...</span>
                    </div>
                    <div v-if="scanStatus.reputation_scan_status == 'in_progress'">
                        <output class="spinner-grow spinner-grow-sm text-warning"></output>
                        <span class="ms-2">In progress...</span>
                    </div>
                </div>

                <div v-else>
                    <div>
                        <table class="table table-bordered">
                            <tbody>
                                <tr v-for="reputation, public_system in scanResult.reputation" :key="public_system">
                                    <td style="width:50%;">{{ public_system }}</td>
                                    <td>
                                        <div>
                                            <span v-if="reputation.is_subdomain_blacklisted"
                                                class="badge text-bg-danger me-2">Subdomain blacklisted</span>
                                            <span v-else class="badge text-bg-success me-2">Subdomain not
                                                blacklisted</span>
                                        </div>

                                        <div>
                                            <span v-if="reputation.is_ip_blacklisted"
                                                class="badge text-bg-danger me-2">IP
                                                blacklisted
                                            </span>
                                            <div v-else>
                                                <span class="badge text-bg-success me-2">IP not blacklisted</span>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div v-if="scanResult?.secrets" class="mb-4">
                <h2 class="text-primary">Secrets</h2>

                <div v-if="scanResult?.secrets?.message === 'Scan not completed'">
                    <div v-if="scanStatus.secrets_scan_status == 'to_be_initiated'">
                        <output class="spinner-grow spinner-grow-sm text-primary"></output>
                        <span class="ms-2">To be initiated...</span>
                    </div>
                    <div v-if="scanStatus.secrets_scan_status == 'in_progress'">
                        <output class="spinner-grow spinner-grow-sm text-warning"></output>
                        <span class="ms-2">In progress...</span>
                    </div>
                </div>

                <div v-else>
                    <div v-for="secrets, public_system in scanResult.secrets" :key="public_system" class="mb-3">
                        <strong>{{ public_system }}</strong>
                        <div v-if="secrets.length > 0">
                            <table v-for="(secret, index) in secrets" class="table table-bordered" :key="index">
                                <tbody>
                                    <tr>
                                        <td style="width:50%;">Description</td>
                                        <td>{{ secret.description }}</td>
                                    </tr>
                                    <tr>
                                        <td>File</td>
                                        <td>{{ secret.file }}</td>
                                    </tr>
                                    <tr>
                                        <td>Line</td>
                                        <td>{{ secret.line }}</td>
                                    </tr>
                                    <tr>
                                        <td>Match</td>
                                        <td class="text-break">{{ secret.match }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div v-else>
                            <span class="badge text-bg-success me-2">
                                No secrets found
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="scanResult?.vulnerabilities" class="mb-4">
                <h2 class="text-primary">Vulnerabilities</h2>

                <div v-if="scanResult?.vulnerabilities?.message === 'Scan not completed'">
                    <div v-if="scanStatus.vulnerability_scan_status == 'to_be_initiated'">
                        <output class="spinner-grow spinner-grow-sm text-primary"></output>
                        <span class="ms-2">To be initiated...</span>
                    </div>
                    <div v-if="scanStatus.vulnerability_scan_status == 'in_progress'">
                        <output class="spinner-grow spinner-grow-sm text-warning"></output>
                        <span class="ms-2">In progress...</span>
                    </div>
                </div>

                <div v-else>
                    <div v-for="vulnerabilities, public_system in scanResult.vulnerabilities" :key="public_system"
                        class="mb-3">
                        <strong>{{ public_system }}</strong>
                        <div v-if="vulnerabilities.length > 0">
                            <table v-for="(vulnerability, index) in vulnerabilities" class="table table-bordered"
                                :key="index">
                                <tbody>
                                    <tr>
                                        <td style="width:50%;">Port</td>
                                        <td>{{ vulnerability.port }}</td>
                                    </tr>
                                    <tr>
                                        <td>Name</td>
                                        <td>{{ vulnerability.name }}</td>
                                    </tr>
                                    <tr>
                                        <td>Summary</td>
                                        <td>{{ vulnerability.summary }}</td>
                                    </tr>
                                    <tr>
                                        <td>Description</td>
                                        <td>{{ vulnerability.description }}</td>
                                    </tr>
                                    <tr v-if="vulnerability.cve_ids.length > 0">
                                        <td>CVE IDs</td>
                                        <td>{{ vulnerability.cve_ids.join(', ') }}</td>
                                    </tr>
                                    <tr>
                                        <td>Solution</td>
                                        <td>{{ vulnerability.solution }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div v-else>
                            <span class="badge text-bg-success me-2">
                                No vulnerabilities found
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="scanResult?.email_in_breach" class="mb-4">
                <h2 class="text-primary">Email in breach</h2>

                <div v-if="scanResult?.email_in_breach?.message === 'Scan not completed'">
                    <div v-if="scanStatus.email_breach_scan_status == 'to_be_initiated'">
                        <output class="spinner-grow spinner-grow-sm text-primary"></output>
                        <span class="ms-2">To be initiated...</span>
                    </div>
                    <div v-if="scanStatus.email_breach_scan_status == 'in_progress'">
                        <output class="spinner-grow spinner-grow-sm text-warning"></output>
                        <span class="ms-2">In progress...</span>
                    </div>
                </div>

                <div v-else>
                    <table class="table table-bordered">
                        <tbody>
                            <tr v-for="(email_in_breach, harvested_email) in scanResult.email_in_breach"
                                :key="harvested_email">
                                <td style="width:50%;">{{ harvested_email }}</td>
                                <td>
                                    <div v-if="email_in_breach.in_breach">
                                        <span class="badge text-bg-danger me-2">In breach</span>
                                        <span>({{ email_in_breach.breach_names.join(', ') }})</span>
                                    </div>
                                    <span v-else class="badge text-bg-success me-2">Not in breach</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import api from '../../../api/index.js'

export default {
    name: 'ScanResultView',
    setup() {
        const route = useRoute(); // Access the route object
        const scan_id = ref(route.params.scan_id); // Extract scan_id from route params
        const isLoading = ref(true);
        const scanResult = ref({});
        const scanStatus = ref({});

        // Fetch scan result from the API when the component is mounted
        const getScanResult = async () => {
            try {
                // Set loading to true before making the request
                isLoading.value = true;

                const response = await api.get(`/scan-result/${scan_id.value}`);

                scanResult.value = response.data
            } catch (error) {
                console.error('Error fetching scan result:', error);
            } finally {
                // Set loading to false once the data has been fetched
                isLoading.value = false;
            }
        };

        // Fetch scan status from the API when the component is mounted
        const getScanStatus = async () => {
            try {
                const response = await api.get(`/scan-status/${scan_id.value}`);

                scanStatus.value = response.data
            } catch (error) {
                console.error('Error fetching scan status:', error);
            }
        };

        onMounted(() => {
            getScanResult();
            getScanStatus();
        });

        return { scan_id, isLoading, scanResult, scanStatus };
    }
}
</script>